import { Link } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const PERKS = [
  "A proven, loved brand — India's most trusted pet care name",
  "Facility blueprint: flooring, breakout zones and hygiene protocol",
  "Training for your team by the country's top trainers",
  "Meal-care, vet-visit and enrichment playbooks from day one",
];

export default function Franchise() {
  return (
    <section data-testid="franchise-page" className="pt-32 pb-28 lg:pt-40">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
            <span className="h-px w-10 bg-bark" aria-hidden="true" />
            Franchise options
          </p>
        </Reveal>
        <h1 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl">
          Bring the pack to <em className="font-editorial italic text-bark">your city.</em>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            We're preparing a franchise program for pet lovers who want to build a premium doggy
            day care and boarding centre the right way. Register your interest and we'll reach out
            first when details go live.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-14 lg:grid-cols-2">
          <Reveal delay={0.15}>
            <ul className="space-y-6">
              {PERKS.map((perk, i) => (
                <li
                  key={perk}
                  data-testid={`franchise-perk-${i + 1}`}
                  className={`flex items-start gap-4 pb-6 ${
                    i > 0 ? "border-t border-ink/8 pt-6" : ""
                  }`}
                >
                  <span
                    className="mt-2 h-2 w-2 shrink-0 rounded-full bg-bark"
                    aria-hidden="true"
                  />
                  <span className="text-base leading-relaxed text-slate-700">{perk}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.25}>
            <form
              data-testid="franchise-interest-form"
              className="rounded-3xl border border-ink/8 bg-white p-8"
              onSubmit={(e) => {
                e.preventDefault();
                toast.success("Interest registered! We'll be in touch as franchise details go live.");
                e.target.reset();
              }}
            >
              <h2 className="font-display text-2xl font-medium tracking-tight text-ink">
                Register your interest
              </h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label
                    htmlFor="franchise-name"
                    className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Full name
                  </label>
                  <input
                    id="franchise-name"
                    data-testid="franchise-name-input"
                    type="text"
                    required
                    placeholder="Your name"
                    className="mt-2 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
                  />
                </div>
                <div>
                  <label
                    htmlFor="franchise-email"
                    className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Email
                  </label>
                  <input
                    id="franchise-email"
                    data-testid="franchise-email-input"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
                  />
                </div>
                <div>
                  <label
                    htmlFor="franchise-city"
                    className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    City
                  </label>
                  <input
                    id="franchise-city"
                    data-testid="franchise-city-input"
                    type="text"
                    required
                    placeholder="Where would you open?"
                    className="mt-2 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="franchise-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-bark px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Register interest
                </button>
                <p className="text-center text-xs text-slate-400">
                  An early interest list — no commitment needed.
                </p>
              </div>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.35}>
          <div className="mt-14">
            <Link
              to="/"
              data-testid="franchise-back-home"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-navy"
            >
              <ArrowLeft
                className="h-4 w-4 text-bark transition-transform duration-200 group-hover:-translate-x-1"
                aria-hidden="true"
              />
              Back to home
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
